// Reconnecting chat WebSocket client (mobile-app §7.3, §7.6). Lives in
// @sportadmin/shared so the Expo app AND the admin SPA use ONE client and the
// reconnect logic can't drift. It is transport-only: the DB/REST history is
// authoritative, so on every (re)connect the consumer refetches to catch
// anything missed while the socket was down.
//
// Auth: browsers can't set a Bearer header on a ws upgrade, so the client
// fetches a short-lived ticket over REST first (getTicket), then connects to
// `…/chat/ws?ticket=…`. On socket close it refetches a fresh ticket and
// reconnects with capped exponential backoff.

export interface ChatFrame {
  type: string; // e.g. "message.new"
  data: unknown;
}

export interface ChatSocketConfig {
  /** Base API origin, e.g. https://api.sportadmin.io (http→ws, https→wss). */
  baseUrl: string;
  /** Org slug whose chat stream to open. */
  orgSlug: string;
  /** Fetch a fresh WS ticket (POST …/chat/ws-ticket → { ticket }). */
  getTicket(): Promise<string>;
  /** Called for every received frame. */
  onFrame(frame: ChatFrame): void;
  /** Called when the socket (re)connects — the cue to refetch history. */
  onReconnect?(): void;
  /** Override for tests / non-browser WebSocket impls. */
  socketFactory?: (url: string) => WebSocketLike;
}

// Minimal structural type so this compiles for both the browser WebSocket and
// React Native's (both are standard) without importing DOM lib types.
export interface WebSocketLike {
  close(): void;
  onopen: ((ev: unknown) => void) | null;
  onclose: ((ev: unknown) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
}

const MAX_BACKOFF_MS = 30_000;
const BASE_BACKOFF_MS = 1_000;

export interface ChatSocketHandle {
  close(): void;
}

// connectChatSocket opens the stream and keeps it alive across drops until
// close() is called. Returns immediately; connection happens asynchronously.
export function connectChatSocket(cfg: ChatSocketConfig): ChatSocketHandle {
  let closed = false;
  let ws: WebSocketLike | null = null;
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const wsBase = cfg.baseUrl.replace(/^http/, 'ws');

  async function open() {
    if (closed) return;
    let ticket: string;
    try {
      ticket = await cfg.getTicket();
    } catch {
      return scheduleReconnect();
    }
    if (closed) return;
    const url = `${wsBase}/api/v1/organizations/${encodeURIComponent(cfg.orgSlug)}/chat/ws?ticket=${encodeURIComponent(ticket)}`;
    const factory = cfg.socketFactory ?? defaultFactory;
    const sock = factory(url);
    ws = sock;

    sock.onopen = () => {
      attempt = 0;
      cfg.onReconnect?.();
    };
    sock.onmessage = (ev) => {
      try {
        const frame = JSON.parse(String(ev.data)) as ChatFrame;
        cfg.onFrame(frame);
      } catch {
        /* ignore malformed frame */
      }
    };
    sock.onerror = () => sock.close();
    sock.onclose = () => {
      ws = null;
      scheduleReconnect();
    };
  }

  function scheduleReconnect() {
    if (closed || timer) return;
    const delay = Math.min(BASE_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS);
    attempt += 1;
    timer = setTimeout(() => {
      timer = null;
      void open();
    }, jitter(delay));
  }

  void open();

  return {
    close() {
      closed = true;
      if (timer) clearTimeout(timer);
      ws?.close();
      ws = null;
    },
  };
}

function jitter(ms: number): number {
  return Math.round(ms * (0.5 + Math.random() / 2));
}

function defaultFactory(url: string): WebSocketLike {
  // `WebSocket` is global in both the browser and React Native.
  return new (globalThis as unknown as { WebSocket: new (u: string) => WebSocketLike }).WebSocket(url);
}
